// The shell the operation-context panels are built from — SEEP and EBS today.
//
// WHAT THESE PANELS ARE. TDI-Brooks does not need this console to tell them how
// to run a seep-hunting or environmental baseline campaign; that is their trade,
// and they have been at it since 1996. What a console CAN do is put the metocean
// facts that bear on one specific operation in one place, at the point they
// clicked, with the gaps named. That is the SVP panel's bargain — "planning-grade,
// confirm with an on-site SV cast" — and it is the one the survey side already
// liked. These follow it exactly.
//
// So the shape is deliberately not a chart or a verdict. It is a short list of
// stated facts, each with what it is FOR, and a closing note saying what the
// panel does not know. A fact with no operational consequence is decoration; a
// panel that implies completeness it does not have is worse than no panel.
//
// A row whose value is genuinely unavailable is NOT dropped. It is printed as an
// absence with the reason, because "we did not fetch that" and "that quantity is
// benign here" look identical once a row disappears — and only one of them is
// safe to act on. See docs/CONVENTIONS.md, "Errors & honesty".

import { infoButton } from "../help/popover.js";
import type { HelpKey } from "../help/types.js";

/** One stated fact. */
export interface Fact {
  /** What it is — "Water depth", "Mixed-layer depth". */
  label: string;
  /** The reading, already in the operator's units. Null prints as an absence. */
  value: string | null;
  /** Why an operator on THIS job cares. One clause, not a paragraph. */
  why?: string;
  /** When `value` is null: why it is missing. Required in that case — an
   *  unexplained blank is the failure this panel exists to avoid. */
  absent?: string;
  /** Marks a fact that limits or qualifies the work rather than describing it. */
  caution?: boolean;
}

export interface FactPanel {
  root: HTMLElement;
  /** Render a set of facts, or null to show the panel's own empty state. */
  render(facts: Fact[] | null, note?: string): void;
  reset(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

export interface FactPanelOptions {
  title: string;
  /** The line under the title — what operation this panel is for. */
  sub: string;
  help: HelpKey;
  /** Shown before a point has been picked. */
  empty: string;
  /** The standing disclaimer, printed under every render. */
  footer: string;
}

export function createFactPanel(parent: HTMLElement, opts: FactPanelOptions): FactPanel {
  const b = el("section", "block");
  const bh = el("div", "bh");
  const t = el("span", "bt", opts.title);
  t.appendChild(infoButton(opts.help));
  bh.append(t, el("span", "bsub", opts.sub));
  b.appendChild(bh);

  const body = el("div", "fp-body");
  b.appendChild(body);
  const foot = el("div", "bsrc source", opts.footer);
  b.appendChild(foot);
  parent.appendChild(b);

  function render(facts: Fact[] | null, note?: string): void {
    body.replaceChildren();
    if (facts === null || facts.length === 0) {
      body.appendChild(el("p", "fp-empty", opts.empty));
      return;
    }
    for (const f of facts) {
      const row = el("div", `fp-row${f.caution === true ? " fp-caution" : ""}`);
      row.appendChild(el("div", "fp-lab", f.label));
      const val = el("div", "fp-val");
      if (f.value === null) {
        val.classList.add("fp-none");
        // The em dash alone would read as zero at a glance, so the reason rides
        // on the same line as the absence rather than under it.
        val.textContent = `— ${f.absent ?? "not available"}`;
      } else {
        val.textContent = f.value;
      }
      row.appendChild(val);
      if (f.why !== undefined) row.appendChild(el("div", "fp-why", f.why));
      body.appendChild(row);
    }
    if (note !== undefined && note !== "") body.appendChild(el("p", "fp-note", note));
  }

  return {
    root: b,
    render,
    reset(): void {
      render(null);
    },
  };
}
